import React from "react";
import { useNavigate } from "react-router-dom";
import { LOGO_URL, MODULES, IMAGES } from "@/data/constants";
import { ProgereText } from "@/components/ProgereText";
import { ModuleIcon } from "@/components/ModuleIcon";
import { Network, ShieldCheck, Zap, RefreshCw } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const POINTS = [
  { icon: Network, key: "p1" },
  { icon: ShieldCheck, key: "p2" },
  { icon: RefreshCw, key: "p3" },
  { icon: Zap, key: "p4" },
];

export function EcosystemSection() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const goToModule = (m) => navigate(m.key === "support" ? "/support" : `/module/${m.key}`);
  return (
    <section id="ecosysteme" className="relative py-24 lg:py-32 overflow-hidden" data-testid="ecosystem-section">
      <div className="absolute inset-0">
        <img src={IMAGES.ecosystem} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-[#050505]/85" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: hub visual */}
          <div className="relative flex items-center justify-center h-[440px] sm:h-[520px] group">
            {/* concentric rings */}
            <div className="absolute w-72 h-72 sm:w-80 sm:h-80 rounded-full border border-[#EAB308]/15" />
            <div className="absolute w-72 h-72 rounded-full border border-[#EAB308]/25 pg-pulse-ring" />
            <div className="absolute w-96 h-96 rounded-full border border-[#dc143c]/12 pg-pulse-ring" style={{ animationDelay: "1.6s" }} />

            {/* rotating orbit with connecting lines + modules */}
            <div className="absolute inset-0 flex items-center justify-center pg-orbit group-hover:[animation-play-state:paused]">
              <svg className="absolute" width="360" height="360" viewBox="-180 -180 360 360">
                {MODULES.map((m, i) => {
                  const angle = (i / MODULES.length) * Math.PI * 2 - Math.PI / 2;
                  const r = 150;
                  const x = Math.cos(angle) * r;
                  const y = Math.sin(angle) * r;
                  const accent = m.color === "gold" ? "#EAB308" : "#dc143c";
                  return (
                    <line key={m.key} x1="0" y1="0" x2={x} y2={y} stroke={accent} strokeOpacity="0.28" strokeWidth="1.5" strokeDasharray="4 5" />
                  );
                })}
              </svg>

              {MODULES.map((m, i) => {
                const angle = (i / MODULES.length) * Math.PI * 2 - Math.PI / 2;
                const r = 150;
                const x = Math.cos(angle) * r;
                const y = Math.sin(angle) * r;
                const accent = m.color === "gold" ? "#EAB308" : "#dc143c";
                return (
                  <div
                    key={m.key}
                    className="absolute"
                    style={{ transform: `translate(${x}px, ${y}px)` }}
                    title={m.name}
                  >
                    <button
                      onClick={() => goToModule(m)}
                      data-testid={`hub-module-${m.key}`}
                      aria-label={m.name}
                      className="pg-orbit-item group-hover:[animation-play-state:paused] flex h-16 w-16 sm:h-[72px] sm:w-[72px] items-center justify-center rounded-2xl border bg-black/85 backdrop-blur transition-transform hover:scale-125 cursor-pointer"
                      style={{ borderColor: `${accent}66`, boxShadow: `0 0 22px -6px ${accent}88` }}
                    >
                      <ModuleIcon name={m.icon} className="w-8 h-8 sm:w-9 sm:h-9" style={{ color: accent }} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* central hub (large, pulsing) */}
            <div className="pg-keep-dark relative z-10 flex items-center justify-center w-40 h-40 sm:w-48 sm:h-48 rounded-3xl border-2 border-[#EAB308]/50 bg-black/80 backdrop-blur-xl pg-glow-pulse pg-float">
              <img src={LOGO_URL} alt="PROGÈRE" className="h-28 w-28 sm:h-36 sm:w-36 object-contain" />
            </div>
          </div>

          {/* Right: text */}
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#dc143c]">
              {t("eco.eyebrow")}
            </span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              {t("eco.titleA")} <ProgereText />
            </h2>
            <p className="mt-4 text-white/60 text-lg leading-relaxed">
              {t("eco.desc")}
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {POINTS.map((p) => (
                <div key={p.key} className="rounded-lg border border-white/10 bg-white/[0.03] p-5" data-testid={`eco-point-${p.key}`}>
                  <p.icon className="w-6 h-6 text-[#EAB308]" />
                  <h3 className="mt-3 font-semibold text-white">{t(`eco.${p.key}t`)}</h3>
                  <p className="mt-1 text-sm text-white/55">{t(`eco.${p.key}`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
